import { supabase } from '../supabase';
import { recordOpportunityActivity } from '../opportunityActivities';

function formatFollowUpForActivity(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Schedules (or clears, when `followUpAt` is null) the next follow-up for an
 * opportunity and logs the matching activity. Mirrors the behaviour of the CRM
 * opportunity-details modal so the message-conversation Lead banner stays in
 * sync with it.
 */
export async function scheduleOpportunityFollowUp(params: {
  businessId: string;
  opportunityId: number;
  customerId?: string | null;
  followUpAt: string | null;
  previousFollowUpAt?: string | null;
}): Promise<void> {
  const {
    businessId,
    opportunityId,
    customerId,
    followUpAt,
    previousFollowUpAt = null,
  } = params;

  if (!supabase || !businessId || !opportunityId) {
    throw new Error('scheduleOpportunityFollowUp: missing required parameters');
  }

  const dbPatch: Record<string, string | null> = {
    next_follow_up_at: followUpAt,
  };
  if (followUpAt) {
    dbPatch.follow_up_due_prompt_dismissed_for_due_at = null;
  }

  const { error } = await supabase
    .from('pipeline_opportunities')
    .update(dbPatch)
    .eq('id', opportunityId);
  if (error) throw error;

  if (customerId) {
    await recordOpportunityActivity({
      businessId,
      customerId,
      activityType: followUpAt ? 'follow_up_scheduled' : 'follow_up_cleared',
      activityData: followUpAt
        ? { next_follow_up_at: followUpAt, scheduled_for: formatFollowUpForActivity(followUpAt) }
        : { previous_follow_up_at: previousFollowUpAt },
      specificOpportunityId: opportunityId,
    });
  }
}

/**
 * Marks the currently-scheduled follow-up complete (logs the completion
 * activity and clears `next_follow_up_at`). Mirrors the CRM modal behaviour.
 */
export async function markOpportunityFollowUpComplete(params: {
  businessId: string;
  opportunityId: number;
  customerId?: string | null;
  previousFollowUpAt: string | null;
}): Promise<void> {
  const { businessId, opportunityId, previousFollowUpAt } = params;
  let customerId = params.customerId ?? null;

  if (!supabase || !businessId || !opportunityId || !previousFollowUpAt) {
    throw new Error('markOpportunityFollowUpComplete: missing required parameters');
  }

  if (!customerId) {
    const { data: row, error: fetchError } = await supabase
      .from('pipeline_opportunities')
      .select('customer_id')
      .eq('id', opportunityId)
      .maybeSingle();
    if (fetchError) throw fetchError;
    customerId = row?.customer_id ?? null;
  }

  if (!customerId) {
    throw new Error('markOpportunityFollowUpComplete: opportunity has no customer');
  }

  const activityRecorded = await recordOpportunityActivity({
    businessId,
    customerId,
    activityType: 'follow_up_completed',
    activityData: {
      completed_follow_up_at: previousFollowUpAt,
      scheduled_for: formatFollowUpForActivity(previousFollowUpAt),
    },
    specificOpportunityId: opportunityId,
  });

  if (!activityRecorded) {
    throw new Error('activity-log-failed');
  }

  const { error } = await supabase
    .from('pipeline_opportunities')
    .update({ next_follow_up_at: null })
    .eq('id', opportunityId);
  if (error) throw error;
}
